import React, { useState, useEffect, useRef, useMemo  } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Text,
  Badge,
  Flex,
  useToast,
  useColorModeValue,
  Spinner,
  Button,
  Icon,
  Image,
  HStack,
  Spacer,
  useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,ModalFooter, Grid,
} from "@chakra-ui/react";
import axios from "axios";
import {  StudentInfo, PracticeAttemptedQuestionSet, AttemptedPracticeQuestionSetStatus, ExamAttemptScore, QuestionSetPracticeAttemptScore, AssessmentAttemptContext, StudentAccountInfo, PracticeExamAttemptScore, AttemptedPracticeQuestion } from '../../../Platform/Common/types';
import { FiClock  } from "react-icons/fi";
import B2CQuestionSetRenderer , { RendererHandle, RendererStats }from "../Components/B2CQuestionSetRenderer";
import { useNavigate } from "react-router-dom";
import eduviclogo from "../../../resources/Eduvictechlogo.png";
import { FaClock, FaHourglassStart, FaFlag } from "react-icons/fa";

import ExamSubmissionConfirmation from "../../../Platform/Components/ExamSubmissionConfirmation";
import { toUtcISOString, parseUtc, fmtLocal } from "../../../utils/dates";
import { useTenant } from "../../../Platform/Routers/TenantContextProvider";
import B2CScoreViewer from "../Components/B2CScoreViewer";
import B2CQsetScoreViewer from "../Components/B2CQsetScoreViewer";

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "green",
  SKIPPED: "red",
  IN_PROGRESS: "purple",
  NOT_STARTED: "gray",
  LOCKED: "gray",
  EXPIRED: "orange",
};

// Color scheme constants
// const TOP_BAR_BG = { light: "gray.100", dark: "gray.700" };
// const QUESTION_CARD_BG = { light: "blue.700", dark: "blue.700" };
// const STATUS_BADGE_BG = { light: "blackAlpha.700", dark: "whiteAlpha.50" };
const TEXT_COLORS = { light: "gray.800", dark: "white" };

// LegendItem: Keep as-is from your code
const LegendItem = ({
  color,
  label,
  flag = false,
  border = false
}: {
  color: string;
  label: string;
  flag?: boolean;
  border?: boolean;
}) => (
  <Flex align="center" gap={2}>
    <Box position="relative" w="20px" h="20px">
      <Box
        w="100%"
        h="100%"
        borderRadius="full"
        bg={color}
        border={border ? '2px solid' : 'none'}
        borderColor={border ? color : 'transparent'}
      />
      {flag && (
        <Box
          position="absolute"
          top="-6px"
          right="-6px"
          color="purple.500"
          fontSize="sm"
        >
          <FaFlag />
        </Box>
      )}
    </Box>
    <Text fontSize="sm" whiteSpace="nowrap">{label}</Text>
  </Flex>
);


// ============================================================
//  LOBBY THEME CONFIG — change one token per intent here
// ============================================================

const LOBBY_THEMES = {
  QUESTION_SET_TIMED:   "blue",
  QUESTION_SET_UNTIMED: "cyan",
  MOCK_EXAM:            "purple",
} as const;

type ChakraColor =
  | "blue" | "teal" | "cyan" | "gray" | "yellow" | "purple" | "blackAlpha"
  | "orange" | "green" | "pink" | "red";

// Derive the active theme token from examContext
const getLobbyTheme = (
  assessmentType?: string,
  timerEnabled?: boolean
): ChakraColor => {
  if (assessmentType === "MOCK_EXAM")    return LOBBY_THEMES.MOCK_EXAM;
  if (timerEnabled === false)            return LOBBY_THEMES.QUESTION_SET_UNTIMED;
  return LOBBY_THEMES.QUESTION_SET_TIMED;
};

// All shade variants derived from a single token
const buildTheme = (color: ChakraColor) => ({
  topBarBg:       { light: `${color}.50`, dark: `gray.700` },
  questionCardBg: { light: `${color}.700`,  dark: `${color}.700` },
  examCardBg: { light: `${color}.50`,  dark: `${color}.700` },
  statusBadgeBg:  { light: `${color}.100`, dark: `${color}.700` },
  activeQSBar:    `${color}.700`,
  connectorActive:`${color}.400`,
  textLight:      "white",
  textDark:       `${color}.100`,
  buttonScheme:   color,
});


const StudentPracticeAttemptLobby = () => {
  
    const navigate = useNavigate();
  const { practice_attempt_id } = useParams<{ practice_attempt_id: string }>();
  const [currentStudent, setCurrentStudent] = useState<StudentAccountInfo>();
  const [examContext, setExamContext] = useState<AssessmentAttemptContext | null>(null);
  const [practiceAttemptedQuestionSet, setPracticeAttemptedQuestionSet] = useState<PracticeAttemptedQuestionSet | null>(null);
  const { tenant } = useTenant();

  const [examAttemptScore, setExamAttemptScore] = useState<PracticeExamAttemptScore | null>(null);

  const [qsetAttemptScore, setQsetAttemptScore] = useState<QuestionSetPracticeAttemptScore | null>(null);


  const [isForceSubmitOpen, setIsForceSubmitOpen] =
  useState(false);

  const [activeQuestionInfo, setActiveQuestionInfo] = useState<{
    index: number;
    maxMarks: number;
    total: number;
  } | null>(null);


  const [loading, setLoading] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  const token = localStorage.getItem("b2c_token");
  const isAuthenticated = Boolean(token);

  const toast = useToast();
  const cardBg = useColorModeValue("gray.100", "gray.700");

  const apiUrl = process.env.REACT_APP_API_URL;


  // Derive theme reactively from examContext
const themeColor = getLobbyTheme(
  examContext?.assessment_type,
  examContext?.timer_enabled
);
const theme = buildTheme(themeColor);

const topBarBg        = useColorModeValue(theme.topBarBg.light,       theme.topBarBg.dark);
const questionCardBg  = useColorModeValue(theme.questionCardBg.light,  theme.questionCardBg.dark);
const examCardBg  = useColorModeValue(theme.examCardBg.light,  theme.examCardBg.dark);
const statusBadgeBg   = useColorModeValue(theme.statusBadgeBg.light,   theme.statusBadgeBg.dark);

const textColor = useColorModeValue(TEXT_COLORS.light, TEXT_COLORS.dark);  


  const markExamInProgress = async (attemptId: string) =>
    axios.put(`${apiUrl}/b2c/practice/assessment-attempt/${attemptId}/mark-in-progress`,null, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
      },
    });

  const markExamComplete = async (attemptId: string) =>
    axios.put(`${apiUrl}/b2c/practice/assessment-attempt/${attemptId}/mark-completed`,null, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
      },
    });

  const autoSubmitQuestionSet = async (qsAttemptId: number) =>{
      // 1. flush debounced calls still pending
    await qsRef.current?.submitAll();
    await axios.put(`${apiUrl}/b2c/practice-question-set-attempt/${qsAttemptId}/submit`,null, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
      },
    });
    setPracticeAttemptedQuestionSet(null);
    setActiveQuestionInfo(null);  // ADD THIS
  }

    const submitQuestionSet = async (qsAttemptId: number) =>{
            // 1. flush debounced calls still pending
      await qsRef.current?.submitAll();
      await axios.put(`${apiUrl}/b2c/practice-question-set-attempt/${qsAttemptId}/submit`,null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
        },
      });

      setActiveQuestionInfo(null);  // ADD THIS
    }

  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);

  // Time updater
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date(Date.now()); 
      setCurrentTime(now);
       const nowMs = now.getTime();    
  
      if (!examContext || !practice_attempt_id) return;
  

      const examStart = parseUtc(examContext.expected_start_time).getTime();
      const examEnd   = parseUtc(examContext.expected_end_time).getTime();
  
      // Mark Exam "In-Progress"
      if (examContext.status === "NOT_STARTED" && nowMs >= examStart) {
        markExamInProgress(practice_attempt_id).then(() => {
          setExamContext((prev) => prev ? { ...prev, status: "IN_PROGRESS" } : prev);
         // fetchExamContext();
        });
      }
  
      // Mark Exam "Completed"
      if (examContext.status === "IN_PROGRESS" && nowMs >= examEnd) {
        // Set status immediately to prevent infinite loop
        setExamContext((prev) => prev ? { ...prev, status: "COMPLETED" } : prev);
      //  markExamComplete(practice_attempt_id).then(fetchExamContext);
      //  fetchExamScore();
        // Same deliberate sequence as confirm-submit -- no exceptions
        completeAttemptAndNavigate(Number(practice_attempt_id));
      }

      // Move active set to next in sequence if active has ended
      const activeQSet = examContext.question_sets.find(
        (qs) => qs.question_set_id === examContext.active_question_set_id
      );

      if (
        activeQSet &&
        (!cooldownUntil || nowMs >= cooldownUntil) // cooldown not set OR has expired
      ) {

      const activeEnd = parseUtc(activeQSet.expected_end_time).getTime();

        if (nowMs >= activeEnd) {
          // Time's up for active set — trigger context refresh to shift cursor
          fetchExamContext();
          setCooldownUntil(nowMs + 30000); // wait 30 seconds before next check
        }
      }
        
      // Auto-submit question sets
      examContext.question_sets.forEach((qs) => {

       const qsEnd = parseUtc(qs.expected_end_time).getTime();
  
        if (qs.status === "IN_PROGRESS" && nowMs >= qsEnd && qs.practice_attempted_question_set_id) {
        //  setExamContext((prev) => prev ? { ...prev, status: "COMPLETED" } : prev);
          autoSubmitQuestionSet(qs.practice_attempted_question_set_id).then(fetchExamContext);
       //   fetchExamScore();
        }
      }
    
    );
    }, 1000);
  
    return () => clearInterval(interval);
  }, [examContext, practice_attempt_id]);

  const fetchCurrentStudent = async () => {
    try {
      const response = await axios.get(`${apiUrl}/b2c/student/account-info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setCurrentStudent(response.data);
    } catch (error) {
      console.error("Error fetching Student Info:", error);
    }
  };

  const fetchExamContext = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/b2c/practice/assessment-attempt/${practice_attempt_id}/context`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        }
      );
      setExamContext(response.data);

       console.log("Exam Context", response.data);

      const activeQS = response.data.question_sets.find(
        (qs : AttemptedPracticeQuestionSetStatus) => qs.question_set_id === response.data.active_question_set_id
      );
  
      if (!activeQS) return null;

      const now     = Date.now();  // live UTC
      const qsStart = parseUtc(activeQS.expected_start_time).getTime();
      const qsEnd   = parseUtc(activeQS.expected_end_time).getTime();
  
/*       if (
        activeQS.status === "IN_PROGRESS" &&
        now >= qsStart &&
        now <= qsEnd
      ) {
        //call for the question set attempt start API

          const response_qset = await axios.post(
            `${apiUrl}/b2c/practice/assessment-attempt/${response.data.practice_attempt_id}/${activeQS.question_set_id}/start`,null, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
              },
            });
    
          setPracticeAttemptedQuestionSet(response_qset.data);

          console.log(
            "response_qset.data.questions.length",
            response_qset.data
          );
    
          console.log("Question Set to be attempted", practiceAttemptedQuestionSet);

      } */

    } catch (error) {
      toast({
        title: "Failed to fetch exam context",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

const fetchExamScore = async () => {
  if (!examContext) return;

  try {
    setLoading(true);

    if (examContext.assessment_type === "MOCK_EXAM") {
      const response = await axios.get(
        `${apiUrl}/b2c/practice-exam-attempt/${practice_attempt_id}/score`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` } }
      );
      setExamAttemptScore(response.data);
    } else {
      const response = await axios.get(
        `${apiUrl}/b2c/practice-qset-attempt/${practice_attempt_id}/score`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` } }
      );
      setQsetAttemptScore(response.data);
    }

  } catch (error) {
    toast({
      title: "Failed to fetch score",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setLoading(false);
  }
};

const completeAttemptAndNavigate = async (practiceAttemptId: number) => {
  const assessmentType = examContext?.assessment_type;
  if (!assessmentType) return;

  // Step 1: mark attempt completed (backfills skipped qsets)
  await markExamComplete(practiceAttemptId.toString());

  // Step 2: score -- lobby is master orchestrator, explicit deliberate call

  let scoreResponse = null;

    try {
      scoreResponse = await axios.get(
        assessmentType === "MOCK_EXAM"
          ? `${apiUrl}/b2c/practice-exam-attempt/${practiceAttemptId}/score`
          : `${apiUrl}/b2c/practice-qset-attempt/${practiceAttemptId}/score`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` } }
      );
    } catch (err) {
      console.warn("Scoring returned no data or failed:", err);
    }

    // Step 3: analytics -- only if we have valid scoring data
    if (scoreResponse?.data) {
      try {
        await axios.get(
          assessmentType === "MOCK_EXAM"
            ? `${apiUrl}/b2c/practice-exam-attempt/${practiceAttemptId}/analytics`
            : `${apiUrl}/b2c/practice-qset-attempt/${practiceAttemptId}/analytics`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` } }
        );
      } catch (err) {
        console.warn("Analytics call failed:", err);
      }
    }

  // Step 4: navigate -- only after everything committed
 // navigate(`/practice/app/practice-attempt/${practiceAttemptId}/view`);
 navigate(`/practice/app/practice-attempt/${practiceAttemptId}/view`, { replace: true });
};


  useEffect(() => {
    if (isAuthenticated) {
    fetchCurrentStudent();
    fetchExamContext();
    }
  }, [practice_attempt_id]);

/*   useEffect(() => {
    if (examContext?.status === "COMPLETED") {
      fetchExamScore();
    }
  }, [examContext?.status]); */


/* useEffect(() => {
  if (examContext?.status === "COMPLETED" && examContext.practice_attempt_id) {
    navigate(`/practice/app/practice-attempt/${examContext.practice_attempt_id}/view`);
  }
}, [examContext?.status]); */


  useEffect(() => {
    if (!isAuthenticated) {
 
        navigate(`/practice/login`);

    }
  }, [isAuthenticated]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getTimerLabel = (start: Date, end: Date) => {

    if (examContext?.timer_enabled)
    {
    const now = currentTime.getTime();

    const startTime = parseUtc(start).getTime();
    const endTime   = parseUtc(end).getTime();

  //  console.log("Start time, End Time and Now",startTime,endTime,now);

    if (now < startTime) {
      const diff = Math.floor((startTime - now) / 1000);
      return `Time to Start: ${formatDuration(diff)}`;
    } else if (now < endTime) {
      const diff = Math.floor((endTime - now) / 1000);
      return `Time Left: ${formatDuration(diff)}`;
    } else {
      return "Time Over";
    }
    }
    else
      return "UNTIMED TEST"
  };

  const getTimerLabelCompact = (start: Date, end: Date) => {
  if (!examContext?.timer_enabled) return "";
  const now = currentTime.getTime();
  const startTime = parseUtc(start).getTime();
  const endTime = parseUtc(end).getTime();

  if (now < startTime) {
    const diff = Math.floor((startTime - now) / 1000);
    return formatDuration(diff); // just the countdown, no label
  } else if (now < endTime) {
    const diff = Math.floor((endTime - now) / 1000);
    return formatDuration(diff);
  } else {
    return "0:00";
  }
};


const getTimerStatus = (start: Date, end: Date) => {
  const now = currentTime.getTime();
  const startTime = parseUtc(start).getTime();
  const endTime = parseUtc(end).getTime();

  if (now < startTime) return "Time to Start";
  if (now < endTime) return "Time Left";
  return "Time Over";
};

const getTimerValue = (start: Date, end: Date) => {
  const now = currentTime.getTime();
  const startTime = parseUtc(start).getTime();
  const endTime = parseUtc(end).getTime();

  if (now < startTime) {
    return formatDuration(Math.floor((startTime - now) / 1000));
  }

  if (now < endTime) {
    return formatDuration(Math.floor((endTime - now) / 1000));
  }

  return "0m 0s";
};

  const TOP_BAR_HEIGHT = "55px";
  const CARD_HEIGHT = "50px";
  const CARD_MIN_WIDTH = "150px";
  const EXAM_CARD_MIN_WIDTH = "200px";
  const CARD_MAX_WIDTH = "350px";
  
  const handleSubmitActiveQuestionSet = async () => {
    const activeQS = examContext?.question_sets.find(
      (qs) => qs.question_set_id === examContext.active_question_set_id
    );

    if (!activeQS || !activeQS.practice_attempted_question_set_id || !examContext?.practice_attempt_id) {
      console.error("Missing active question set or attempt ID.");
      return;
    }
   
    handleShowStats();

  };

/* 
const handleConfirmSubmit = async () => {
  setIsConfirmationOpen(false); // close modal
  const activeQS = examContext?.question_sets.find(
    (qs) => qs.question_set_id === examContext.active_question_set_id
  );

  if (!activeQS || !activeQS.practice_attempted_question_set_id || !examContext?.practice_attempt_id) {
    console.error("Missing active question set or attempt ID.");
    return;
  }

  try {
    // Step 1: Submit the current question set
    await submitQuestionSet(activeQS.practice_attempted_question_set_id);

    // Step 2: Identify current index and whether it's the last question set
    const qsList = examContext.question_sets;
    const currentIndex = qsList.findIndex(qs => qs.question_set_id === activeQS.question_set_id);
    const isLastSet = currentIndex === qsList.length - 1;

    console.log("To check if it is indeed last set", isLastSet)

    if (isLastSet) {


        console.log(
        "Last Question Set submitted. Completing Practice Attempt..."
      );

      await markExamComplete(
        examContext.practice_attempt_id.toString()
      );

      // IMPORTANT:
      // Fetch latest context BEFORE rendering completed screen
      await fetchExamContext();

      // Then fetch score
      await fetchExamScore();


    } else {

      console.log("Not the last QSet now set the exam context by making next question set as active")
      // Step 4: If not last, just update active_question_set_id locally
      const nextQuestionSetId = qsList[currentIndex + 1].question_set_id;

       setExamContext({
        ...examContext,
        active_question_set_id: nextQuestionSetId,
      }); 
    }

    console.log("Fetch complete exam context - this is important whether this gets fired or not")
    // Step 5: Refresh the full exam context from backend
    await fetchExamContext();
    setPracticeAttemptedQuestionSet(null);
  } catch (error) {
    console.error("Failed to submit question set:", error);
    alert("Failed to submit question set. Please try again.");
  }
};
   */

const handleConfirmSubmit = async () => {
  setIsConfirmationOpen(false);
  const activeQS = examContext?.question_sets.find(
    (qs) => qs.question_set_id === examContext.active_question_set_id
  );

  if (!activeQS || !activeQS.practice_attempted_question_set_id || !examContext?.practice_attempt_id) {
    console.error("Missing active question set or attempt ID.");
    return;
  }

  try {
    await submitQuestionSet(activeQS.practice_attempted_question_set_id);

    const qsList = examContext.question_sets;
    const currentIndex = qsList.findIndex(qs => qs.question_set_id === activeQS.question_set_id);
    const isLastSet = currentIndex === qsList.length - 1;

    if (isLastSet) {
      // Last QSet -- lobby orchestrates full completion sequence then navigates
      await completeAttemptAndNavigate(examContext.practice_attempt_id);
    } else {
      // Not last -- advance to next QSet, stay in lobby
      const nextQuestionSetId = qsList[currentIndex + 1].question_set_id;
      setExamContext({
        ...examContext,
        active_question_set_id: nextQuestionSetId,
      });
      await fetchExamContext();
      setPracticeAttemptedQuestionSet(null);
    }
  } catch (error) {
    console.error("Failed to submit question set:", error);
    alert("Failed to submit question set. Please try again.");
  }
};
const isStartingQSRef = useRef(false);

const handleQuestionSetStart = async (questionSetId: number, practiceAttemptId: number) => {
  // Gate: prevent concurrent calls
  if (isStartingQSRef.current) return;
  isStartingQSRef.current = true;

  try {
    const response = await axios.post(
      `${apiUrl}/b2c/practice/assessment-attempt/${practiceAttemptId}/${questionSetId}/start`,
      null,
      { headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` } }
    );
    console.log(
  "START response Q1-3",
  response.data.attempted_questions
    .slice(0, 3)
    .map((q: AttemptedPracticeQuestion) => [
      q.sequence_no,
      q.status,
      q.selected_option_ids,
      q.text_response,
    ])
);
    setPracticeAttemptedQuestionSet(response.data);  // ← single place this is set (except null on submit)
    // Refresh context AFTER we have the QS data, not before
    await fetchExamContext();
  } catch (err) {
    console.error("Failed to start question set:", err);
    alert("Failed to start question set. Please try again.");
  } finally {
    isStartingQSRef.current = false;
  }
};

    const { isOpen, onOpen, onClose } = useDisclosure();
  const qsRef = useRef<RendererHandle>(null);
  const [stats, setStats] = useState<RendererStats | null>(null);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const handleShowStats = () => {
    if (qsRef.current) {
      const latestStats = qsRef.current.getStats();
      setStats(latestStats);
      setIsConfirmationOpen(true);
    }
  }


  const autoStartedRef = useRef<number | null>(null);



useEffect(() => {
  if (!examContext) return;

  const activeQS = examContext.question_sets.find(
    (qs) => qs.question_set_id === examContext.active_question_set_id
  );

  if (!activeQS) return;

  const now = currentTime.getTime();
  const qsStart = parseUtc(activeQS.expected_start_time).getTime();
  const qsEnd = parseUtc(activeQS.expected_end_time).getTime();

  const isReadyToStart =
    activeQS.status === "NOT_STARTED" && now >= qsStart && now <= qsEnd;

  if (isReadyToStart && autoStartedRef.current !== activeQS.question_set_id) {
    autoStartedRef.current = activeQS.question_set_id;
    // Pass IDs explicitly — no stale closure risk
    handleQuestionSetStart(activeQS.question_set_id, examContext.practice_attempt_id);
  }
}, [examContext, currentTime]);


// After fetchExamContext sets examContext, check if we need to recover an IN_PROGRESS QS
// Add this useEffect:
useEffect(() => {
  if (!examContext || practiceAttemptedQuestionSet) return; // already have it

  const activeQS = examContext.question_sets.find(
    (qs) => qs.question_set_id === examContext.active_question_set_id
  );

  if (activeQS?.status === "IN_PROGRESS" && examContext.practice_attempt_id) {
    // Recovery: QS is in progress but we have no local data (e.g. page reload)
    handleQuestionSetStart(activeQS.question_set_id, examContext.practice_attempt_id);
  }
}, [examContext?.active_question_set_id]); // only re-run when active QS changes


const handleForceSubmitExam = async () => {
  setIsForceSubmitOpen(false);

  if (!examContext?.practice_attempt_id) return;

  try {
    const activeQS = examContext.question_sets.find(
      (qs) => qs.question_set_id === examContext.active_question_set_id
    );

    if (
      activeQS &&
      activeQS.status === "IN_PROGRESS" &&
      activeQS.practice_attempted_question_set_id
    ) {
      await submitQuestionSet(activeQS.practice_attempted_question_set_id);
    }

    // mark_practice_exam_attempt_completed handles backfilling
    // skipped qsets -- completeAttemptAndNavigate calls markExamComplete
    // which triggers that logic, so no separate backfill call needed here.
    setPracticeAttemptedQuestionSet(null);
    await completeAttemptAndNavigate(examContext.practice_attempt_id);

  } catch (error) {
    console.error("Failed force submitting exam:", error);
    alert("Failed to submit exam. Please try again.");
  }
};

/*   const handleForceSubmitExam = async () => {

    setIsForceSubmitOpen(false);

    if (!examContext?.practice_attempt_id) {
      return;
    }

    try {

      const activeQS = examContext.question_sets.find(
        (qs) =>
          qs.question_set_id ===
          examContext.active_question_set_id
      );

      // Submit active question set if currently running
      if (
        activeQS &&
        activeQS.status === "IN_PROGRESS" &&
        activeQS.practice_attempted_question_set_id
      ) {

        console.log(
          "Submitting active question set before force completing exam"
        );

        await submitQuestionSet(
          activeQS.practice_attempted_question_set_id
        );
      }

      console.log(
        "Force completing practice attempt"
      );

      await markExamComplete(
        examContext.practice_attempt_id.toString()
      );

      await fetchExamContext();

      await fetchExamScore();

      setPracticeAttemptedQuestionSet(null);

    } catch (error) {

      console.error(
        "Failed force submitting exam",
        error
      );

      alert(
        "Failed to submit exam. Please try again."
      );

    }
  };
 */
 
  return (
    <Box as="main" mx="auto" p={0}>
      {loading ? (
        <Flex align="center" justify="center" mt={6}>
          <Spinner size="lg" />
        </Flex>
        // EXAM TOP BAR CODE
      ) : examContext ? (
        <Flex
          align="center"
          justify="space-between"
          position="sticky"
          top={0}
          zIndex={10}
          background={topBarBg}
      //    shadow={"md"}
          px={4}
          py={1}
          h={TOP_BAR_HEIGHT}
        >
          <Flex align="center" overflowX="auto" overflowY="hidden" wrap="nowrap">
          <Image 
            src={eduviclogo} 
            alt="Logo" 
            boxSize={TOP_BAR_HEIGHT}
            objectFit="contain" 
            border="md" 
            background={`${themeColor}.700`} 
            mr={2}
            />

            {(examContext.assessment_type == "MOCK_EXAM") && (  
                <>

                  {/* Exam Card */}
                  <Flex
                    direction="column"
                    justify="center"
                    bg={topBarBg}
                    px={1}
                    py={1}
                    minW={EXAM_CARD_MIN_WIDTH}
                    maxW={CARD_MAX_WIDTH}
                    h={TOP_BAR_HEIGHT}
                    ml={2}
                    color={textColor}
                    borderRadius="none"
                    backgroundColor={examCardBg}
                    overflow="hidden"
                  >
                    <Flex align="center" justify="space-between"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        maxW={CARD_MAX_WIDTH}
                      >
                        {examContext.title}
                      </Text>

                    </Flex>
                    {(examContext.status == "IN_PROGRESS" ||  examContext.status == "NOT_STARTED") && (examContext.assessment_type=="MOCK_EXAM") && (
                      <Flex align="center" mt={0} gap={1}>
                        <Icon as={FiClock} w={3} h={3} />

                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          whiteSpace="nowrap"
                        >
                          Exam {getTimerStatus(
                            parseUtc(examContext.expected_start_time),
                            parseUtc(examContext.expected_end_time)
                          )}
                        </Text>

                        <Badge
                          bg="gray.900"
                          border="1px solid"
                          borderColor="green.600"
                          color="green.300"
                          fontFamily="mono"
                          fontSize="xs"
                          fontWeight="bold"
                          px={1.5}
                          py={0}
                          borderRadius="sm"
                        >
                          {getTimerValue(
                            parseUtc(examContext.expected_start_time),
                            parseUtc(examContext.expected_end_time)
                          )}
                        </Badge>
                      </Flex>
                      )}   
                  </Flex>

                  
                {/* Question Sets */}
                {examContext.question_sets
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((qs, index) => {
                    const isActive = qs.question_set_id === examContext.active_question_set_id;

                    const connectorColor =
                      qs.status === "COMPLETED"   ? "green.400" :
                      qs.status === "IN_PROGRESS" ? theme.connectorActive :
                      qs.status === "SKIPPED"     ? "red.400" :
                      "gray.300";

                    return (
                      <React.Fragment key={qs.question_set_id}>

                        <Box
                          w="30px"
                          h="4px"
                          bgColor={connectorColor}
                          mx={2}
                          borderRadius="md"
                          flexShrink={0}
                          display={{ base: "none", md: "inline" }}
                        />

                        {/* Card: position="relative" so badge can anchor to top-right */}
                        <Flex
                          direction="column"
                          justify="center"
                          position="relative"
                          bg={isActive ? questionCardBg : `${themeColor}.600`}
                          px={3}
                          pt={4}                      /* extra top padding so title clears the badge */
                          pb={1}
                          minW={CARD_MIN_WIDTH}
                          maxW={CARD_MAX_WIDTH}
                          w="fit-content"             /* ← grows to content up to maxW */
                          h={CARD_HEIGHT}
                          borderRadius="sm"
                          textAlign="left"
                          overflow="visible"          /* ← allow badge to sit at top edge */
                          opacity={isActive ? 1 : 0.4}
                          flexShrink={0}
                          display={{ base: "none", md: "inline" }}
                        >
                          {/* Badge anchored top-right of card */}
                          <Badge
                            position="absolute"
                            top="3px"
                            right="4px"
                            colorScheme={STATUS_COLORS[qs.status]}
                            fontSize="0.6em"
                            variant="subtle"
                            px={1}
                          >
                            {qs.status.replace(/_/g, " ")}
                          </Badge>

                          {/* Title now has full card width without competing with badge */}
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            color="white"
                          >
                            {qs.title}
                          </Text>

                        </Flex>

                      </React.Fragment>
                    );
                  })}

                </>

              )}

              {examContext.assessment_type === "QUESTION_SET" && (
                <Text
                  position="absolute"
                  left="50%"
                  transform="translateX(-50%)"
                  fontSize="lg"
                  fontWeight="bold"
                  color={textColor}
                  whiteSpace="nowrap"
                  pointerEvents="none"
                >
                  {examContext.timer_enabled ? "Timed Practice Test"  : "Untimed Practice Test"}
                  {"\t"} {examContext.question_sets[0].title}
                </Text>
              )}
             
          </Flex>

          {/* RIGHT SIDE: Actions */}
          <Flex
            align="center"
            gap={4}
            bg={topBarBg}
            px={3}
            py={1}
            borderRadius="none"
            h={CARD_HEIGHT}
            color={textColor}
            minW="120px"
            justify="flex-end"
          >
            {/* Force Submit Action */}
            {examContext.status =="IN_PROGRESS" && examContext.assessment_type === "MOCK_EXAM" && (
              <Button
                colorScheme="red"
                variant="solid"
                size="md"
                shadow="2xl"
                onClick={() => setIsForceSubmitOpen(true)}
              >
                Finish Now
              </Button>
            )}
              
          </Flex>
        </Flex>
      ) : null}

      <Modal
        isOpen={isForceSubmitOpen}
        onClose={() => setIsForceSubmitOpen(false)}
        isCentered
      >
        <ModalOverlay />

        <ModalContent>

          <ModalHeader>
            Finish Exam Now
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>

            <Text mb={4}>
              You are about to end this Mock Exam attempt
              immediately.
            </Text>

            <Text mb={4}>
              Any remaining Question Sets that have not
              yet been attempted will be marked as skipped.
            </Text>

            <Text mb={4}>
              Any Question Set currently in progress will
              be submitted automatically.
            </Text>

            <Text
              color="red.500"
              fontWeight="bold"
            >
              This action cannot be undone.
            </Text>

          </ModalBody>

          <ModalFooter>

            <Button
              colorScheme="red"
              mr={3}
              onClick={handleForceSubmitExam}
            >
              Force Submit Exam
            </Button>

            <Button
              variant="ghost"
              onClick={() =>
                setIsForceSubmitOpen(false)
              }
            >
              Cancel
            </Button>

          </ModalFooter>

        </ModalContent>
      </Modal>


      {/* Question Set Bar and Questions Rendering */}

      {examContext && (() => {
          const activeQS = examContext.question_sets.find(
            (qs) => qs.question_set_id === examContext.active_question_set_id
          );

          if (!activeQS) return null;

          const now = currentTime.getTime();
          // const qsStart = new Date(activeQS.expected_start_time).getTime();
          // const qsEnd = new Date(activeQS.expected_end_time).getTime();

           const qsStart = parseUtc(activeQS.expected_start_time).getTime();
          const qsEnd   = parseUtc(activeQS.expected_end_time).getTime();

          const isReadyToStart =
            activeQS.status === "NOT_STARTED" && now >= qsStart && now <= qsEnd;

          const badgeText = isReadyToStart ? "Click Start To Begin  >>" : activeQS.status;
          const badgeColor = isReadyToStart ? "blue" : STATUS_COLORS[activeQS.status];

          return (
              <>
              {/* Question Set Top Bar Container - GRAY Background */}
            {/* QUESTION SET BAR: Title left | Timer centre | Submit right */}

              {/* B2C Question Set Top Bar Container - GRAY Background */}
              <Box
                borderRadius="sm"
                p={2}
                bg={theme.activeQSBar}
                boxShadow="md"
                position="sticky"
               // top="45px"
               top={TOP_BAR_HEIGHT}
                zIndex="sticky"
              >
                <Flex
                  align="center"
                  justify="space-between"
                  minH="40px"
                  px={{ base: 2, md: 6 }}
                  gap={2}
                  wrap="nowrap"
                >
                  {/* LEFT — timer: icon + time, verbose hidden on mobile */}
                  {/* LEFT — timer */}
                  <Flex align="center" gap={2} flexShrink={0}>
                    <Icon as={FiClock} w={4} h={4} color="gray.300" flexShrink={0} />

                    {examContext?.timer_enabled ? (
                      <>
                        {/* Mobile */}
                        <Badge
                          display={{ base: "inline-flex", md: "none" }}
                          px={2}
                          py={1}
                          bg="gray.900"
                          border="1px solid"
                          borderColor="green.600"
                          color="green.300"
                          fontSize="sm"
                          fontWeight="bold"
                          fontFamily="mono"
                        >
                          {getTimerValue(
                            parseUtc(activeQS.expected_start_time),
                            parseUtc(activeQS.expected_end_time)
                          )}
                        </Badge>

                        {/* Desktop */}
                        <Flex
                          display={{ base: "none", md: "inline-flex" }}
                          align="center"
                          gap={2}
                        >
                          <Text
                            fontSize="md"
                            color="gray.300"
                            fontWeight="semibold"
                            whiteSpace="nowrap"
                          >
                            {getTimerStatus(
                              parseUtc(activeQS.expected_start_time),
                              parseUtc(activeQS.expected_end_time)
                            )}
                          </Text>

                          <Badge
                            px={3}
                            py={1}
                            bg="gray.900"
                            border="1px solid"
                            borderColor="green.600"
                            color="green.300"
                            fontSize="md"
                            fontWeight="bold"
                            fontFamily="mono"
                            letterSpacing="0.05em"
                            borderRadius="md"
                            minW="90px"
                            textAlign="center"
                          >
                            {getTimerValue(
                              parseUtc(activeQS.expected_start_time),
                              parseUtc(activeQS.expected_end_time)
                            )}
                          </Badge>
                        </Flex>
                      </>
                    ) : (
                      <Badge colorScheme="gray" fontSize="xs" ml={1}>
                        UNTIMED
                      </Badge>
                    )}
                  </Flex>

                  {/* CENTRE — question info, shrinks gracefully on mobile */}
                  <Flex align="center" justify="center" flex={1} minW={0}>
                    {activeQuestionInfo ? (
                      <Text
                        fontWeight="bold"
                        color="white"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        fontSize={{ base: "md", md: "lg" }}
                      >
                        {/* Mobile: compact Q x/y */}
                        <Text as="span" display={{ base: "inline", md: "none" }}>
                          Q {activeQuestionInfo.index + 1}/{activeQuestionInfo.total}
                        </Text>
                        {/* Desktop: full label with max marks */}
                        <Text as="span" display={{ base: "none", md: "inline" }}>
                          Question {activeQuestionInfo.index + 1} of {activeQuestionInfo.total}
                          &nbsp;·&nbsp;
                          Max Marks: {activeQuestionInfo.maxMarks}
                        </Text>
                      </Text>
                    ) : (
                      <Text
                        fontWeight="bold"
                        color="white"
                        fontSize={{ base: "xs", md: "lg" }}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {activeQS.title}
                      </Text>
                    )}
                  </Flex>

                  {/* RIGHT — submit always visible, sized for mobile */}
                  <Box flexShrink={0}>
                    {activeQS.status === "IN_PROGRESS" && !isReadyToStart ? (
                      <Button
                        colorScheme={theme.buttonScheme}
                        variant="solid"
                        size={{ base: "sm", md: "lg" }}
                        shadow="2xl"
                        onClick={handleSubmitActiveQuestionSet}
                      >
                        Submit
                      </Button>
                    ) : (
                      /* Invisible placeholder preserves layout balance on desktop only */
                      <Box display={{ base: "none", md: "block" }} w="80px" />
                    )}
                  </Box>
                </Flex>
              </Box>

                {/* Renderer Container - WHITE Background */}
                {activeQS.status === "IN_PROGRESS" && practiceAttemptedQuestionSet && (
                  <Box
                    borderRadius="sm"
                    boxShadow="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                  <B2CQuestionSetRenderer
                    practiceAttemptedQuestionSetProp={practiceAttemptedQuestionSet}
                    ref={qsRef}
                    onQuestionChange={(index, question) => {
                      setActiveQuestionInfo({
                        index,
                        maxMarks: question.max_marks ?? 0,
                        total: practiceAttemptedQuestionSet.attempted_questions.length,
                      });
                    }}
                  />
                  </Box>
                )}

                <Modal isOpen={isConfirmationOpen} onClose={() => setIsConfirmationOpen(false)} isCentered size="xl">
                  <ModalOverlay />
                  <ModalContent borderRadius="lg" px={4}>
                    <ModalHeader fontWeight="bold" textAlign="center">Review & Confirm Submission</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {stats && (
                          <Box borderWidth="1px" borderRadius="md" p={4} mb={4} >
                            <Text fontWeight="semibold" mb={3}>
                              Question Summary
                            </Text>

                            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                              {/* Answered Section */}
                              <LegendItem 
                                color="green.400" 
                                label={`Answered (No Flag): ${stats.answeredWithoutFlag}`} 
                              />
                              <LegendItem 
                                color="green.400" 
                                label={`Answered + Flagged: ${stats.answeredWithFlag}`} 
                                flag 
                              />

                              {/* Visited but Not Answered Section */}
                              <LegendItem 
                                color="red.400" 
                                label={`Visited (No Flag): ${stats.visitedWithoutFlag}`} 
                              />
                              <LegendItem 
                                color="red.400" 
                                label={`Visited + Flagged: ${stats.visitedWithFlag}`} 
                                flag 
                              />

                              {/* Not Visited */}
                              <LegendItem 
                                color="gray.400" 
                                label={`Not Visited: ${stats.notVisited}`} 
                              />
                            </Grid>

                            <Text mt={4} fontWeight="medium">
                              <strong>Total Questions:</strong> {stats.total}
                            </Text>
                          </Box>
                        )}

                        <Text color="red.500" fontWeight="medium" mt={2} textAlign="center">
                          Are you sure you want to submit this question set? This action cannot be undone.
                        </Text>
                      </ModalBody>


                    <ModalFooter>
                      <Button colorScheme="blue" mr={3} onClick={handleConfirmSubmit}>
                        Confirm Submit
                      </Button>
                      <Button variant="ghost" onClick={() => setIsConfirmationOpen(false)}>
                        Cancel
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>

              </>
          );
        })()}
{/* {examContext && examContext.status === "COMPLETED" && (
  <Box>
    {examContext.assessment_type === "MOCK_EXAM" ? (
      examAttemptScore ? (
        <B2CScoreViewer scoreData={examAttemptScore} />
      ) : (
        currentStudent &&
        <ExamSubmissionConfirmation
          student_email={currentStudent.email}
          student_given_name={currentStudent.first_name}
          exam_title={examContext.title}
          student_end_time={
            examContext.student_end_time
              ? fmtLocal(examContext.student_end_time, {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }).replace(",", " at")
              : ""
          }
        />
      )
    ) : (
      qsetAttemptScore ? (
        <B2CQsetScoreViewer attempted_question_set_score={qsetAttemptScore} />
      ) : (
        currentStudent &&
        <ExamSubmissionConfirmation
          student_email={currentStudent.email}
          student_given_name={currentStudent.first_name}
          exam_title={examContext.title}
          student_end_time={
            examContext.student_end_time
              ? fmtLocal(examContext.student_end_time, {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }).replace(",", " at")
              : ""
          }
        />
      )
    )}
  </Box>
)} */}
    </Box>
    
  );
};

export default StudentPracticeAttemptLobby;
